/**
 * Internationalization (i18n) for Module 4.1 - Public Tender Request Module
 * @module publictender-module-request
 *
 * Structure: Each key contains an object with translations for pt, en, and es
 * Example: { label: { pt: 'Texto', en: 'Text', es: 'Texto' } }
 *
 * Note: Common labels (actions, messages, etc.) are imported from @/domains/shared/i18n
 * Use the helper functions from @/chassis/helpers/i18n for translations
 */

export const i18n = {
    // ==========================================
    // Module Information
    // ==========================================
    'module.name': {
        pt: 'Módulo de Licitações',
        en: 'Public Tenders Module',
        es: 'Módulo de Licitaciones',
    },
    'module.description': {
        pt: 'Gestão completa de licitações públicas',
        en: 'Complete public tender management',
        es: 'Gestión completa de licitaciones públicas',
    },

    // ==========================================
    // Common Actions & Messages (Module Scope)
    // ==========================================
    'common.view': {
        pt: 'Visualizar',
        en: 'View',
        es: 'Ver',
    },
    'common.edit': {
        pt: 'Editar',
        en: 'Edit',
        es: 'Editar',
    },
    'common.activate': {
        pt: 'Ativar',
        en: 'Activate',
        es: 'Activar',
    },
    'common.deactivate': {
        pt: 'Desativar',
        en: 'Deactivate',
        es: 'Desactivar',
    },
    'common.loading': {
        pt: 'Carregando...',
        en: 'Loading...',
        es: 'Cargando...',
    },
    'common.updatingStatus': {
        pt: 'Atualizando status...',
        en: 'Updating status...',
        es: 'Actualizando estado...',
    },

    // ==========================================
    // Public Tenders - General
    // ==========================================
    'publicTenders.singular': {
        pt: 'Licitação',
        en: 'Public Tender',
        es: 'Licitación',
    },
    'publicTenders.plural': {
        pt: 'Licitações',
        en: 'Public Tenders',
        es: 'Licitaciones',
    },
    'publicTenders.description': {
        pt: 'Gestão de licitações públicas',
        en: 'Public tender management',
        es: 'Gestión de licitaciones públicas',
    },

    'publicTenders.sections.general': {
        pt: 'Informações gerais',
        en: 'General information',
        es: 'Información general',
    },
    'publicTenders.sections.configuration': {
        pt: 'Configurações e benefícios',
        en: 'Settings and benefits',
        es: 'Configuraciones y beneficios',
    },
    'publicTenders.sections.budgetAndPhases': {
        pt: 'Orçamento e Fases',
        en: 'Budget and Phases',
        es: 'Presupuesto y Fases',
    },
    'publicTenders.sections.budgetAndPhases.documentSubmission': {
        pt: 'Envio de documentação do fornecedor',
        en: 'Supplier document submission',
        es: 'Envío de documentación del proveedor',
    },
    'publicTenders.sections.budgetAndPhases.documentSubmission.description': {
        pt: 'Define quando o fornecedor deverá enviar a documentação de habilitação',
        en: 'Defines when supplier must submit qualification documents',
        es: 'Define cuándo el proveedor deberá enviar la documentación de habilitación',
    },
    'publicTenders.sections.budgetAndPhases.fundingSource': {
        pt: 'Origem dos Recursos',
        en: 'Funding Source',
        es: 'Origen de los Recursos',
    },
    'publicTenders.sections.budgetAndPhases.fundingSource.description': {
        pt: 'Selecione a origem dos recursos financeiros para este processo',
        en: 'Select the financial resource source for this process',
        es: 'Seleccione el origen de los recursos financieros para este proceso',
    },
    'publicTenders.sections.budgetAndPhases.hiddenBudget': {
        pt: 'Orçamento Sigiloso',
        en: 'Confidential Budget',
        es: 'Presupuesto Confidencial',
    },
    'publicTenders.sections.budgetAndPhases.hiddenBudget.description': {
        pt: 'O orçamento estimado não será visível para os fornecedores',
        en: 'The estimated budget will not be visible to suppliers',
        es: 'El presupuesto estimado no será visible para los proveedores',
    },
    'publicTenders.sections.budgetAndPhases.proposalGuarantee': {
        pt: 'Exige Garantia de Proposta',
        en: 'Requires Bid Bond',
        es: 'Exige Garantía de Propuesta',
    },
    'publicTenders.sections.budgetAndPhases.proposalGuarantee.description': {
        pt: 'O fornecedor anexa a garantia de proposta (Art. 58 da Lei 14.133) para enviar a proposta',
        en: 'The supplier attaches the bid bond (Art. 58 of Law 14.133) to submit the proposal',
        es: 'El proveedor adjunta la garantía de propuesta (Art. 58 de la Ley 14.133) para enviar la propuesta',
    },
    'publicTenders.fields.documentSubmissionMode': {
        pt: 'Modo de Envio de Documentação',
        en: 'Document Submission Mode',
        es: 'Modo de Envío de Documentación',
    },
    'publicTenders.fields.documentSubmissionMode.initialProposal': {
        pt: 'Envio na Proposta Inicial',
        en: 'Submit at Initial Proposal',
        es: 'Envío en la Propuesta Inicial',
    },
    'publicTenders.fields.documentSubmissionMode.initialProposal.description': {
        pt: 'Habilita o fornecedor a enviar documentação de habilitação já na tela de proposta inicial.',
        en: 'Enables supplier to submit qualification documents at the initial proposal stage.',
        es: 'Habilita al proveedor a enviar documentación de habilitación en la etapa de propuesta inicial.',
    },
    'publicTenders.fields.documentSubmissionMode.judgementPhase': {
        pt: 'Envio na fase de Julgamento',
        en: 'Submit at Judgement Phase',
        es: 'Envío en la fase de Juicio',
    },
    'publicTenders.fields.documentSubmissionMode.judgementPhase.description': {
        pt: 'Fornecedor envia a documentação apenas na tela de Julgamento. O pregoeiro define o prazo.',
        en: 'Supplier submits documents only at the Judgement stage. Auctioneer defines the deadline.',
        es: 'Proveedor envía la documentación solo en la pantalla de Juicio. El pregoeiro define el plazo.',
    },
    'publicTenders.fields.documentSubmissionDeadlineHours.hint': {
        pt: 'Informe o prazo que o fornecedor terá para enviar a documentação após ser convocado.',
        en: 'Enter the deadline the supplier will have to submit the documents after being summoned.',
        es: 'Informe el plazo que el proveedor tendrá para enviar la documentación después de ser convocado.',
    },
    'publicTenders.fields.isPhaseInversion.label': {
        pt: 'Usar inversão de fases',
        en: 'Use phase inversion',
        es: 'Usar inversión de fases',
    },
    'publicTenders.fields.isPhaseInversion.description': {
        pt: 'Pregoeiro visualiza a documentação na tela de Propostas e pode desclassificar fornecedores.',
        en: 'Auctioneer views documentation on the Proposals screen and can disqualify suppliers.',
        es: 'Pregoeiro visualiza la documentación en la pantalla de Propuestas y puede descalificar proveedores.',
    },
    'publicTenders.fields.fundingSource.federal.badge': {
        pt: 'Integra com TransfereGov',
        en: 'Integrates with TransfereGov',
        es: 'Integra con TransfereGov',
    },
    'publicTenders.sections.conditions': {
        pt: 'Condições e critérios',
        en: 'Conditions and criteria',
        es: 'Condiciones y criterios',
    },
    'publicTenders.sections.object': {
        pt: 'Objeto',
        en: 'Object',
        es: 'Objeto',
    },
    'publicTenders.sections.timeline': {
        pt: 'Cronograma e prazos',
        en: 'Timeline and deadlines',
        es: 'Cronograma y plazos',
    },
    'publicTenders.tabs.details': {
        pt: 'Dados Principais',
        en: 'Main Data',
        es: 'Datos Principales',
    },
    'publicTenders.tabs.lotsAndItems': {
        pt: 'Lotes e Itens',
        en: 'Lots and Items',
        es: 'Lotes e Ítems',
    },
    'publicTenders.tabs.lotsAndItems.emptyTitle': {
        pt: 'Gerencie os vínculos da licitação',
        en: 'Manage the tender relationships',
        es: 'Gestiona los vínculos de la licitación',
    },
    'publicTenders.tabs.lotsAndItems.readyDescription': {
        pt: 'Acesse a área de vínculos para manter lotes, itens e demais associações da licitação.',
        en: 'Open the relationships area to maintain lots, items, and other tender associations.',
        es: 'Accede al área de vínculos para mantener lotes, ítems y otras asociaciones de la licitación.',
    },
    'publicTenders.tabs.lotsAndItems.blockedDescription': {
        pt: 'Salve a licitação para liberar o gerenciamento de vínculos.',
        en: 'Save the tender to unlock relationships management.',
        es: 'Guarda la licitación para habilitar la gestión de vínculos.',
    },
    'publicTenders.tabs.lotsAndItems.cta': {
        pt: 'Ir para vínculos',
        en: 'Go to relationships',
        es: 'Ir a vínculos',
    },
    'publicTenders.tabs.history': {
        pt: 'Histórico',
        en: 'History',
        es: 'Historial',
    },
    'publicTenders.tabs.history.emptyTitle': {
        pt: 'Registro de alterações',
        en: 'Change log',
        es: 'Registro de cambios',
    },
    'publicTenders.tabs.history.emptyDescription': {
        pt: 'Acompanhe os principais marcos de criação, atualização e status deste cadastro.',
        en: 'Track the main creation, update, and status milestones for this record.',
        es: 'Sigue los principales hitos de creación, actualización y estado de este registro.',
    },

    // ==========================================
    // Public Tenders - Quick Edit (Lots)
    // ==========================================
    'publicTenders.quickEditLot.title': {
        pt: 'Edição rápida do lote',
        en: 'Quick lot edit',
        es: 'Edición rápida del lote',
    },
    'publicTenders.quickEditLot.subtitle1': {
        pt: 'Faça ajustes rápidos no lote sem sair desta tela.',
        en: 'Make quick adjustments to the lot without leaving this screen.',
        es: 'Haz ajustes rápidos en el lote sin salir de esta pantalla.',
    },
    'publicTenders.quickEditLot.subtitle2': {
        pt: 'Salve para aplicar imediatamente.',
        en: 'Save to apply immediately.',
        es: 'Guarda para aplicar de inmediato.',
    },
    'publicTenders.quickEditLot.loading': {
        pt: 'Carregando dados do lote...',
        en: 'Loading lot data...',
        es: 'Cargando datos del lote...',
    },
    'publicTenders.quickEditLot.actions.close': {
        pt: 'Fechar',
        en: 'Close',
        es: 'Cerrar',
    },
    'publicTenders.quickEditLot.actions.cancel': {
        pt: 'Cancelar',
        en: 'Cancel',
        es: 'Cancelar',
    },
    'publicTenders.quickEditLot.actions.save': {
        pt: 'Salvar alterações',
        en: 'Save changes',
        es: 'Guardar cambios',
    },
    'publicTenders.quickEditLot.states.saving': {
        pt: 'Salvando...',
        en: 'Saving...',
        es: 'Guardando...',
    },

    'publicTenders.quickEditItem.title': {
        pt: 'Edição rápida do item',
        en: 'Quick item edit',
        es: 'Edición rápida del ítem',
    },
    'publicTenders.quickEditItem.subtitle1': {
        pt: 'Faça ajustes rápidos no item sem sair desta tela.',
        en: 'Make quick adjustments to the item without leaving this screen.',
        es: 'Haz ajustes rápidos en el ítem sin salir de esta pantalla.',
    },
    'publicTenders.quickEditItem.subtitle2': {
        pt: 'Salve para aplicar imediatamente.',
        en: 'Save to apply immediately.',
        es: 'Guarda para aplicar de inmediato.',
    },
    'publicTenders.quickEditItem.loading': {
        pt: 'Carregando dados do item...',
        en: 'Loading item data...',
        es: 'Cargando datos del ítem...',
    },
    'publicTenders.quickEditItem.actions.close': {
        pt: 'Fechar',
        en: 'Close',
        es: 'Cerrar',
    },
    'publicTenders.quickEditItem.actions.cancel': {
        pt: 'Cancelar',
        en: 'Cancel',
        es: 'Cancelar',
    },
    'publicTenders.quickEditItem.actions.save': {
        pt: 'Salvar alterações',
        en: 'Save changes',
        es: 'Guardar cambios',
    },
    'publicTenders.quickEditItem.states.saving': {
        pt: 'Salvando...',
        en: 'Saving...',
        es: 'Guardando...',
    },
    'publicTenders.quickEdit.selectPlaceholder': {
        pt: 'Selecione',
        en: 'Select',
        es: 'Seleccione',
    },

    // ==========================================
    // Public Tenders - Lot form (used in quick edit)
    // ==========================================
    'publicTenders.lots.form.sections.basic': {
        pt: 'Informações básicas',
        en: 'Basic Information',
        es: 'Información básica',
    },
    'publicTenders.lots.form.sections.financial': {
        pt: 'Financeiro',
        en: 'Financial',
        es: 'Financiero',
    },
    'publicTenders.lots.form.sections.policies': {
        pt: 'Políticas ME/EPP',
        en: 'ME/EPP Policies',
        es: 'Políticas ME/EPP',
    },
    'publicTenders.lots.form.sections.additional': {
        pt: 'Configurações adicionais',
        en: 'Additional Settings',
        es: 'Configuraciones adicionales',
    },
    'publicTenders.lots.form.fields.tenderId.label': {
        pt: 'ID da Licitação',
        en: 'Tender ID',
        es: 'ID de la licitación',
    },
    'publicTenders.lots.form.fields.tenderId.filter.id': {
        pt: 'ID',
        en: 'ID',
        es: 'ID',
    },
    'publicTenders.lots.form.fields.tenderId.filter.publicTenderCode': {
        pt: 'Código da Licitação',
        en: 'Tender Code',
        es: 'Código de licitación',
    },
    'publicTenders.lots.form.fields.tenderId.filter.administrativeProcessCode': {
        pt: 'Processo Administrativo',
        en: 'Administrative Process',
        es: 'Proceso administrativo',
    },
    'publicTenders.lots.form.fields.tenderId.placeholder': {
        pt: 'Busque e selecione a licitação',
        en: 'Search and select the tender',
        es: 'Busque y seleccione la licitación',
    },
    'publicTenders.lots.form.fields.tenderId.column.id': {
        pt: 'ID',
        en: 'ID',
        es: 'ID',
    },
    'publicTenders.lots.form.fields.tenderId.column.publicTenderCode': {
        pt: 'Código da Licitação',
        en: 'Tender Code',
        es: 'Código de licitación',
    },
    'publicTenders.lots.form.fields.tenderId.column.administrativeProcessCode': {
        pt: 'Processo Administrativo',
        en: 'Administrative Process',
        es: 'Proceso administrativo',
    },
    'publicTenders.lots.form.fields.itemType.label': {
        pt: 'Tipo de Itens',
        en: 'Item Types',
        es: 'Tipos de Ítems',
    },
    'publicTenders.lots.form.fields.itemType.placeholder': {
        pt: 'Selecione o tipo',
        en: 'Select a type',
        es: 'Seleccione el tipo',
    },
    'publicTenders.lots.form.fields.itemType.options.product': {
        pt: 'Material',
        en: 'Material',
        es: 'Material',
    },
    'publicTenders.lots.form.fields.itemType.options.service': {
        pt: 'Serviço',
        en: 'Service',
        es: 'Servicio',
    },
    'publicTenders.lots.form.fields.lotNumber.label': {
        pt: 'Número do lote',
        en: 'Lot Number',
        es: 'Número del lote',
    },
    'publicTenders.lots.form.fields.lotNumber.placeholder': {
        pt: 'ex.: 1, 2, 3',
        en: 'e.g., 1, 2, 3',
        es: 'p. ej., 1, 2, 3',
    },
    'publicTenders.lots.form.fields.title.placeholder': {
        pt: 'Informe o título do lote',
        en: 'Enter lot title',
        es: 'Ingresa el título del lote',
    },
        'publicTenders.lots.form.fields.bidFormat.label': {
            pt: 'Formato de Lance',
            en: 'Bid Format',
            es: 'Formato de Oferta',
        },
        'publicTenders.lots.form.fields.bidFormat.placeholder': {
            pt: 'Selecione o formato',
            en: 'Select the format',
            es: 'Seleccione o formato',
        },
        'publicTenders.lots.form.fields.bidFormat.options.global': {
            pt: 'Lote (Global)',
            en: 'Lot (Global)',
            es: 'Lote (Global)',
        },
        'publicTenders.lots.form.fields.bidFormat.options.item': {
            pt: 'Item (Unitário)',
            en: 'Item (Unitary)',
            es: 'Ítem (Unitario)',
        },
        'publicTenders.lots.form.fields.bidFormat.options.kit': {
            pt: 'Kit',
            en: 'Kit',
            es: 'Kit',
        },
        'publicTenders.lots.form.fields.bidStepValue.label': {
            pt: 'Valor Intervalo entre Lances (R$)',
            en: 'Bid Step Value (R$)',
            es: 'Valor de Incremento (R$)',
        },
        'publicTenders.lots.form.fields.bidStepValue.placeholder': {
            pt: 'Selecione o incremento mínimo',
            en: 'Select the minimum increment',
            es: 'Seleccione el incremento mínimo',
        },
        'publicTenders.lots.form.fields.referenceValue.label': {
            pt: 'Valor de Referência (R$)',
            en: 'Reference Value (R$)',
            es: 'Valor de Referencia (R$)',
        },
        'publicTenders.lots.form.fields.referenceValue.placeholder': {
            pt: 'Informe o valor de referência',
            en: 'Enter the reference value',
            es: 'Ingrese el valor de referencia',
        },
    'publicTenders.lots.form.fields.description.label': {
        pt: 'Descrição',
        en: 'Description',
        es: 'Descripción',
    },
    'publicTenders.lots.form.fields.description.placeholder': {
        pt: 'Descreva o lote',
        en: 'Enter lot description',
        es: 'Ingresa la descripción del lote',
    },
    'publicTenders.lots.form.fields.judgmentCriteria.label': {
        pt: 'Critério de julgamento',
        en: 'Judgment Criteria',
        es: 'Criterio de juzgamiento',
    },
    'publicTenders.lots.form.fields.judgmentCriteria.placeholder': {
        pt: 'ex.: Menor preço global',
        en: 'e.g., Lowest global price',
        es: 'p. ej., Menor precio global',
    },
    'publicTenders.lots.form.fields.estimatedValue.label': {
        pt: 'Valor estimado (R$)',
        en: 'Estimated Value (R$)',
        es: 'Valor estimado (R$)',
    },
    'publicTenders.lots.form.fields.estimatedValue.placeholder': {
        pt: '0,00',
        en: '0.00',
        es: '0,00',
    },
    'publicTenders.lots.form.fields.status.label': {
        pt: 'Status',
        en: 'Status',
        es: 'Estado',
    },
    'publicTenders.lots.form.fields.status.placeholder': {
        pt: 'Selecione o status',
        en: 'Select status',
        es: 'Seleccione el estado',
    },
    'publicTenders.lots.form.fields.quota.label': {
        pt: 'Cota recomendada ME/EPP (%)',
        en: 'Recommended ME/EPP Quota (%)',
        es: 'Cuota ME/EPP recomendada (%)',
    },
    'publicTenders.lots.form.fields.quota.placeholder': {
        pt: '0,00',
        en: '0.00',
        es: '0,00',
    },
    'publicTenders.lots.form.fields.exclusive.label': {
        pt: 'Exclusivo para ME/EPP',
        en: 'Exclusive for ME/EPP',
        es: 'Exclusivo para ME/EPP',
    },
    'publicTenders.lots.form.fields.exclusive.helper': {
        pt: 'Este lote é exclusivo para microempresas e empresas de pequeno porte',
        en: 'This lot is exclusively for micro-enterprises and small businesses',
        es: 'Este lote es exclusivo para microempresas y pequeñas empresas',
    },
    'publicTenders.lots.form.fields.systematicRegistration.label': {
        pt: 'Registro de Preços (SRP)',
        en: 'Systematic Registration',
        es: 'Registro sistemático',
    },
    'publicTenders.lots.form.fields.systematicRegistration.helper': {
        pt: 'Este lote é para registro de preços (SRP - Sistema de Registro de Preços)',
        en: 'This lot is for systematic registration (SRP - Price Registration System)',
        es: 'Este lote es para registro sistemático (SRP - Sistema de Registro de Precios)',
    },

    // ==========================================
    // Public Tenders - Actions (Domain-Specific)
    // ==========================================
    'publicTenders.actions.publish': {
        pt: 'Publicar',
        en: 'Publish',
        es: 'Publicar',
    },
    'publicTenders.actions.suspend': {
        pt: 'Suspender',
        en: 'Suspend',
        es: 'Suspender',
    },
    'publicTenders.actions.finalize': {
        pt: 'Finalizar',
        en: 'Finalize',
        es: 'Finalizar',
    },
    'publicTenders.actions.reopen': {
        pt: 'Reabrir',
        en: 'Reopen',
        es: 'Reabrir',
    },

    // ==========================================
    // Public Tenders - Fields
    // ==========================================
    'publicTenders.fields.modality': {
        pt: 'Modalidade',
        en: 'Modality',
        es: 'Modalidad',
    },
    'publicTenders.fields.contractingAgency': {
        pt: 'Órgão Promotor',
        en: 'Contracting Agency',
        es: 'Organismo Contratante',
    },
    'publicTenders.fields.estimatedValue': {
        pt: 'Valor Estimado',
        en: 'Estimated Value',
        es: 'Valor Estimado',
    },
    'publicTenders.fields.submissionDeadline': {
        pt: 'Prazo de Submissão',
        en: 'Submission Deadline',
        es: 'Plazo de Presentación',
    },
    'publicTenders.fields.openingDate': {
        pt: 'Data de Abertura',
        en: 'Opening Date',
        es: 'Fecha de Apertura',
    },
    'publicTenders.fields.proposalCount': {
        pt: 'Propostas',
        en: 'Proposals',
        es: 'Propuestas',
    },
    'publicTenders.fields.viewCount': {
        pt: 'Visualizações',
        en: 'Views',
        es: 'Visualizaciones',
    },
    'publicTenders.fields.tenderNumber': {
        pt: 'Número do Edital',
        en: 'Notice Number',
        es: 'Número del Edicto',
    },
    'publicTenders.fields.publicTenderCode': {
        pt: 'Código da Licitação',
        en: 'Public Tender Code',
        es: 'Código de Licitación',
    },
    'publicTenders.fields.title': {
        pt: 'Descrição do Objeto',
        en: 'Object Description',
        es: 'Descripción del Objeto',
    },
    'publicTenders.fields.description': {
        pt: 'Descrição longa do objeto',
        en: 'Long object description',
        es: 'Descripción larga del objeto',
    },
    'publicTenders.placeholders.objectDescription': {
        pt: 'Descreva o objeto da licitação',
        en: 'Describe the tender object',
        es: 'Describa el objeto de la licitación',
    },
    'publicTenders.fields.administrativeProcess': {
        pt: 'Processo Administrativo',
        en: 'Administrative Process',
        es: 'Proceso Administrativo',
    },
    'publicTenders.fields.yearOfReference': {
        pt: 'Ano de referência',
        en: 'Reference year',
        es: 'Año de referencia',
    },
    'publicTenders.fields.contractType': {
        pt: 'Tipo de Contrato',
        en: 'Contract Type',
        es: 'Tipo de Contrato',
    },
    'publicTenders.fields.fundingSource': {
        pt: 'Origem dos Recursos',
        en: 'Funding Source',
        es: 'Origen de los Recursos',
    },
    'publicTenders.fields.pncpCode': {
        pt: 'Código PNCP',
        en: 'PNCP Code',
        es: 'Código PNCP',
    },
    'publicTenders.fields.regulationUsed': {
        pt: 'Regulamento utilizado',
        en: 'Regulation used',
        es: 'Reglamento utilizado',
    },
    'publicTenders.fields.judgementCriteria': {
        pt: 'Critério de Julgamento',
        en: 'Judgement Criteria',
        es: 'Criterio de Juicio',
    },
    'publicTenders.fields.disputeType': {
        pt: 'Tipo de Disputa',
        en: 'Dispute Type',
        es: 'Tipo de Disputa',
    },
    'publicTenders.fields.supplierRegionalityBenefit': {
        pt: 'Benefício Regional do Fornecedor',
        en: 'Supplier Regionality Benefit',
        es: 'Beneficio Regional del Proveedor',
    },
    'publicTenders.fields.supplierSizeBenefit': {
        pt: 'Benefício de Porte do Fornecedor',
        en: 'Supplier Size Benefit',
        es: 'Beneficio de Tamaño del Proveedor',
    },
    'publicTenders.fields.isHiddenBudget': {
        pt: 'Orçamento Sigiloso',
        en: 'Confidential Budget',
        es: 'Presupuesto Confidencial',
    },
    'publicTenders.fields.isPhaseInversion': {
        pt: 'Inversão de Fases',
        en: 'Phase Inversion',
        es: 'Inversión de Fases',
    },
    'publicTenders.fields.isPhaseInversion.hint': {
        pt: 'Habilita o envio de documentação de habilitação já na etapa de proposta inicial',
        en: 'Enables submission of qualification documents during the initial proposal stage',
        es: 'Habilita el envío de documentación de habilitación en la etapa de propuesta inicial',
    },
    'publicTenders.fields.documentSubmissionDeadlineHours': {
        pt: 'Prazo para Envio (horas)',
        en: 'Submission Deadline (hours)',
        es: 'Plazo para Envío (horas)',
    },
    'publicTenders.fields.bidDecimalPlaces': {
        pt: 'Casas Decimais do Lance',
        en: 'Bid Decimal Places',
        es: 'Decimales de la Oferta',
    },
    'publicTenders.fields.publishedDate': {
        pt: 'Data de Publicação',
        en: 'Published Date',
        es: 'Fecha de Publicación',
    },
    'publicTenders.fields.proposalSubmissionStartDate': {
        pt: 'Início do Envio das Propostas',
        en: 'Proposal Submission Start Date',
        es: 'Fecha de Inicio de Presentación',
    },
    'publicTenders.fields.proposalSubmissionEndDate': {
        pt: 'Fim do Envio das Propostas',
        en: 'Proposal Submission End Date',
        es: 'Fecha de Fin de Presentación',
    },
    'publicTenders.fields.clarificationEndDate': {
        pt: 'Data Fim de Esclarecimentos',
        en: 'Clarification End Date',
        es: 'Fecha Fin de Aclaraciones',
    },
    'publicTenders.fields.impeachmentEndDate': {
        pt: 'Data Fim de Impugnação',
        en: 'Impeachment End Date',
        es: 'Fecha Fin de Impugnación',
    },
    'publicTenders.fields.disputeStartDate': {
        pt: 'Data de Início da Disputa',
        en: 'Dispute Start Date',
        es: 'Fecha de Inicio de Disputa',
    },
    'publicTenders.fields.proposalValidityMonths': {
        pt: 'Validade da Proposta (dias)',
        en: 'Proposal Validity (days)',
        es: 'Validez de la Propuesta (días)',
    },
    'publicTenders.fields.appealManifestationTime': {
        pt: 'Tempo de Manifestação de Recurso (minutos)',
        en: 'Appeal Manifestation Time (minutes)',
        es: 'Tiempo de Manifestación de Recurso (minutos)',
    },
    'publicTenders.fields.appealManifestationTime.hint': {
        pt: 'Mínimo 10 minutos (Art. 40 §1° Lei 14.133/2021)',
        en: 'Minimum 10 minutes (Art. 40 §1° Law 14.133/2021)',
        es: 'Mínimo 10 minutos (Art. 40 §1° Ley 14.133/2021)',
    },
    'publicTenders.fields.appealManifestationTime.validation.min10': {
        pt: 'Mínimo 10 minutos (Art. 40 §1° Lei 14.133)',
        en: 'Minimum 10 minutes (Art. 40 §1° Law 14.133)',
        es: 'Mínimo 10 minutos (Art. 40 §1° Ley 14.133)',
    },
    'publicTenders.fields.appealReceptionFormTimeHours': {
        pt: 'Prazo para Recebimento do Recurso (Dias Úteis)',
        en: 'Appeal Reception Deadline (Business Days)',
        es: 'Plazo para Recepción del Recurso (Días Hábiles)',
    },
    'publicTenders.fields.counterArgumentsReceptionFormTimeHours': {
        pt: 'Prazo para Contrarrazões (Dias Úteis)',
        en: 'Counter-Arguments Deadline (Business Days)',
        es: 'Plazo para Contrarréplicas (Días Hábiles)',
    },
    'publicTenders.fields.appealEndDate': {
        pt: 'Data Fim de Recurso',
        en: 'Appeal End Date',
        es: 'Fecha Fin de Recurso',
    },
    'publicTenders.fields.supportTeam': {
        pt: 'Equipe de Apoio',
        en: 'Support Team',
        es: 'Equipo de Apoyo',
    },
    'publicTenders.fields.auctioneer': {
        pt: 'Pregoeiro / Condutor',
        en: 'Auctioneer / Conductor',
        es: 'Subastador / Conductor',
    },
    'publicTenders.fields.superiorAuthority': {
        pt: 'Autoridade Superior',
        en: 'Superior Authority',
        es: 'Autoridad Superior',
    },
    'publicTenders.fields.contactPhone': {
        pt: 'Telefone para contato',
        en: 'Contact phone',
        es: 'Teléfono de contacto',
    },
    'publicTenders.fields.contactEmail': {
        pt: 'Email para contato',
        en: 'Contact email',
        es: 'Correo de contacto',
    },
    'publicTenders.fields.noticeFile': {
        pt: 'Edital',
        en: 'Notice',
        es: 'Pliego',
    },
    'publicTenders.fields.requiredProposalDocuments': {
        pt: 'Documentos exigidos para habilitação do fornecedor',
        en: 'Required documents for proposal',
        es: 'Documentos requeridos para la propuesta',
    },
    'publicTenders.help.noticeFileRequired': {
        pt: 'Selecione o edital completo.',
        en: 'Select the complete notice.',
        es: 'Seleccione el pliego completo.',
    },

    // ==========================================
    // Public Tenders - Status
    // ==========================================
    'publicTenders.status.draft': {
        pt: 'Rascunho',
        en: 'Draft',
        es: 'Borrador',
    },

    // ==========================================
    // Public Tenders - Sections (extra)
    // ==========================================
    'publicTenders.sections.appealDeadlines': {
        pt: 'Prazos recursais',
        en: 'Appeal deadlines',
        es: 'Plazos de recursos',
    },
    'publicTenders.sections.contact': {
        pt: 'Contato do promotor',
        en: 'Promoter contact',
        es: 'Contacto del promotor',
    },
    'publicTenders.sections.documents': {
        pt: 'Documentos',
        en: 'Documents',
        es: 'Documentos',
    },
    'publicTenders.sections.requiredProposalDocuments': {
        pt: 'Documentos exigidos para habilitação do fornecedor',
        en: 'Required documents for supplier qualification',
        es: 'Documentos requeridos para habilitación del proveedor',
    },
    'publicTenders.sections.additionalDocuments': {
        pt: 'Documentos adicionais',
        en: 'Additional documents',
        es: 'Documentos adicionales',
    },
    'publicTenders.sections.team': {
        pt: 'Equipe responsável',
        en: 'Responsible team',
        es: 'Equipo responsable',
    },

    'publicTenders.messages.readOnly': {
        pt: 'Você possui acesso somente leitura a este cadastro.',
        en: 'You have read-only access to this record.',
        es: 'Tienes acceso de solo lectura a este registro.',
    },
    'publicTenders.messages.teamInfo': {
        pt: 'As opções disponíveis abaixo são equipes e pessoas já vinculadas ao órgão promotor.',
        en: 'The options below are teams and people already linked to the promoting agency.',
        es: 'Las opciones a continuación son equipos y personas ya vinculadas al organismo promotor.',
    },
    'publicTenders.messages.noticeFileSelected': {
        pt: 'Arquivo selecionado:',
        en: 'Selected file:',
        es: 'Archivo seleccionado:',
    },
    'publicTenders.messages.saved': {
        pt: 'Licitação salva com sucesso.',
        en: 'Tender saved successfully.',
        es: 'Licitación guardada con éxito.',
    },
    'publicTenders.lookup.contractingAgency.filters.id': {
        pt: 'ID',
        en: 'ID',
        es: 'ID',
    },
    'publicTenders.lookup.contractingAgency.filters.name': {
        pt: 'Nome',
        en: 'Name',
        es: 'Nombre',
    },
    'publicTenders.lookup.contractingAgency.filters.cnpj': {
        pt: 'CNPJ',
        en: 'CNPJ',
        es: 'CNPJ',
    },
    'publicTenders.lookup.contractingAgency.columns.id': {
        pt: 'ID',
        en: 'ID',
        es: 'ID',
    },
    'publicTenders.lookup.contractingAgency.columns.name': {
        pt: 'Órgão',
        en: 'Agency',
        es: 'Organismo',
    },
    'publicTenders.lookup.contractingAgency.columns.cnpj': {
        pt: 'CNPJ',
        en: 'CNPJ',
        es: 'CNPJ',
    },
    'publicTenders.status.published': {
        pt: 'Publicada',
        en: 'Published',
        es: 'Publicada',
    },
    'publicTenders.status.in_progress': {
        pt: 'Em Andamento',
        en: 'In Progress',
        es: 'En Curso',
    },
    'publicTenders.status.finalized': {
        pt: 'Finalizada',
        en: 'Finalized',
        es: 'Finalizada',
    },
    'publicTenders.status.suspended': {
        pt: 'Suspensa',
        en: 'Suspended',
        es: 'Suspendida',
    },
    'publicTenders.status.cancelled': {
        pt: 'Cancelada',
        en: 'Cancelled',
        es: 'Cancelada',
    },

    // ==========================================
    // Public Tenders - Modalities
    // ==========================================
    'publicTenders.modality.pregao_eletronico': {
        pt: 'Pregão Eletrônico',
        en: 'Electronic Bidding',
        es: 'Subasta Electrónica',
    },
    'publicTenders.modality.pregao_presencial': {
        pt: 'Pregão Presencial',
        en: 'In-Person Bidding',
        es: 'Subasta Presencial',
    },
    'publicTenders.modality.concorrencia': {
        pt: 'Concorrência',
        en: 'Public Competition',
        es: 'Concurrencia',
    },
    'publicTenders.modality.tomada_precos': {
        pt: 'Tomada de Preços',
        en: 'Price Quotation',
        es: 'Toma de Precios',
    },
    'publicTenders.modality.convite': {
        pt: 'Convite',
        en: 'Invitation',
        es: 'Invitación',
    },
    'publicTenders.modality.concurso': {
        pt: 'Concurso',
        en: 'Contest',
        es: 'Concurso',
    },
    'publicTenders.modality.leilao': {
        pt: 'Leilão',
        en: 'Auction',
        es: 'Subasta',
    },
    'publicTenders.modality.dispensa': {
        pt: 'Dispensa',
        en: 'Waiver',
        es: 'Dispensa',
    },
    'publicTenders.modality.inexigibilidade': {
        pt: 'Inexigibilidade',
        en: 'Non-Requirement',
        es: 'Inexigibilidad',
    },

    // ==========================================
    // Public Tenders - Messages (Domain-Specific)
    // ==========================================
    'publicTenders.messages.published': {
        pt: 'Licitação publicada com sucesso',
        en: 'Tender published successfully',
        es: 'Licitación publicada con éxito',
    },
    'publicTenders.messages.suspended': {
        pt: 'Licitação suspensa',
        en: 'Tender suspended',
        es: 'Licitación suspendida',
    },
    'publicTenders.messages.cancelled': {
        pt: 'Licitação cancelada',
        en: 'Tender cancelled',
        es: 'Licitación cancelada',
    },
    'publicTenders.messages.finalized': {
        pt: 'Licitação finalizada',
        en: 'Tender finalized',
        es: 'Licitación finalizada',
    },

    // ==========================================
    // Public Tenders - Stats
    // ==========================================
    'publicTenders.stats.total': {
        pt: 'Total de Licitações',
        en: 'Total Tenders',
        es: 'Total de Licitaciones',
    },
    'publicTenders.stats.active': {
        pt: 'Ativas',
        en: 'Active',
        es: 'Activas',
    },
    'publicTenders.stats.inProgress': {
        pt: 'Em Andamento',
        en: 'In Progress',
        es: 'En Curso',
    },
    'publicTenders.stats.finalized': {
        pt: 'Finalizadas',
        en: 'Finalized',
        es: 'Finalizadas',
    },

    // ==========================================
    // Lots - General
    // ==========================================
    'lots.singular': {
        pt: 'Lote',
        en: 'Lot',
        es: 'Lote',
    },
    'lots.plural': {
        pt: 'Lotes',
        en: 'Lots',
        es: 'Lotes',
    },
    'lots.description': {
        pt: 'Gestão de lotes da licitação',
        en: 'Tender lots management',
        es: 'Gestión de lotes de licitación',
    },
    'lots.actions.addItem': {
        pt: 'Adicionar Item',
        en: 'Add Item',
        es: 'Agregar Ítem',
    },

    // ==========================================
    // Lots - Fields (Specific)
    // ==========================================
    'lots.fields.number': {
        pt: 'Número',
        en: 'Number',
        es: 'Número',
    },
    'lots.fields.publicTenderId': {
        pt: 'Licitação',
        en: 'Tender',
        es: 'Licitación',
    },
    'lots.fields.categoryId': {
        pt: 'Categoria',
        en: 'Category',
        es: 'Categoría',
    },
    'lots.fields.estimatedValue': {
        pt: 'Valor Estimado',
        en: 'Estimated Value',
        es: 'Valor Estimado',
    },
    'lots.fields.judgmentCriteria': {
        pt: 'Critério de Julgamento',
        en: 'Judgment Criteria',
        es: 'Criterio de Juicio',
    },
    'lots.fields.quotaForME_EPP': {
        pt: 'Quota ME/EPP Recomendada',
        en: 'Recommended ME/EPP Quota',
        es: 'Cuota ME/EPP Recomendada',
    },
    'lots.fields.minimumValue': {
        pt: 'Valor Mínimo',
        en: 'Minimum Value',
        es: 'Valor Mínimo',
    },
    'lots.fields.maximumValue': {
        pt: 'Valor Máximo',
        en: 'Maximum Value',
        es: 'Valor Máximo',
    },
    'lots.fields.quantity': {
        pt: 'Quantidade',
        en: 'Quantity',
        es: 'Cantidad',
    },
    'lots.fields.unit': {
        pt: 'Unidade',
        en: 'Unit',
        es: 'Unidad',
    },

    // ==========================================
    // Items - General
    // ==========================================
    'items.singular': {
        pt: 'Item',
        en: 'Item',
        es: 'Ítem',
    },
    'items.plural': {
        pt: 'Itens',
        en: 'Items',
        es: 'Ítems',
    },
    'items.description': {
        pt: 'Gestão de itens dos lotes',
        en: 'Lot items management',
        es: 'Gestión de ítems de lotes',
    },

    // Items - Sections
    'items.sections.general': {
        pt: 'Informações gerais',
        en: 'General information',
        es: 'Información general',
    },
    'items.sections.quantityValues': {
        pt: 'Quantidades e valores',
        en: 'Quantities and values',
        es: 'Cantidades y valores',
    },

    // ==========================================
    // Items - Fields (Specific)
    // ==========================================
    'items.fields.title': {
        pt: 'Título',
        en: 'Title',
        es: 'Título',
    },
    'items.fields.lotId': {
        pt: 'Lote',
        en: 'Lot',
        es: 'Lote',
    },
    'items.fields.itemType': {
        pt: 'Tipo do item',
        en: 'Item type',
        es: 'Tipo de ítem',
    },
    'items.fields.categoryId': {
        pt: 'Categoria',
        en: 'Category',
        es: 'Categoría',
    },
    'items.fields.quantity': {
        pt: 'Quantidade',
        en: 'Quantity',
        es: 'Cantidad',
    },
    'items.fields.unit': {
        pt: 'Unidade',
        en: 'Unit',
        es: 'Unidad',
    },
    'items.fields.unitPrice': {
        pt: 'Preço Unitário',
        en: 'Unit Price',
        es: 'Precio Unitario',
    },
    'items.fields.totalPrice': {
        pt: 'Preço Total',
        en: 'Total Price',
        es: 'Precio Total',
    },
    'items.fields.specifications': {
        pt: 'Especificações',
        en: 'Specifications',
        es: 'Especificaciones',
    },
    'items.fields.catmatCode': {
        pt: 'Código CATMAT',
        en: 'CATMAT Code',
        es: 'Código CATMAT',
    },
    'items.fields.catserCode': {
        pt: 'Código CATSER',
        en: 'CATSER Code',
        es: 'Código CATSER',
    },
    'items.fields.isSustainable': {
        pt: 'Item Sustentável',
        en: 'Sustainable Item',
        es: 'Ítem Sostenible',
    },

    // ==========================================
    // Items - Units
    // ==========================================
    'items.units.un': {
        pt: 'Unidade',
        en: 'Unit',
        es: 'Unidad',
    },
    'items.units.kg': {
        pt: 'Quilograma',
        en: 'Kilogram',
        es: 'Kilogramo',
    },
    'items.units.m': {
        pt: 'Metro',
        en: 'Meter',
        es: 'Metro',
    },
    'items.units.m2': {
        pt: 'Metro Quadrado',
        en: 'Square Meter',
        es: 'Metro Cuadrado',
    },
    'items.units.m3': {
        pt: 'Metro Cúbico',
        en: 'Cubic Meter',
        es: 'Metro Cúbico',
    },
    'items.units.l': {
        pt: 'Litro',
        en: 'Liter',
        es: 'Litro',
    },
    'items.units.hr': {
        pt: 'Hora',
        en: 'Hour',
        es: 'Hora',
    },
    'items.units.day': {
        pt: 'Dia',
        en: 'Day',
        es: 'Día',
    },
    'items.units.month': {
        pt: 'Mês',
        en: 'Month',
        es: 'Mes',
    },
    'items.units.year': {
        pt: 'Ano',
        en: 'Year',
        es: 'Año',
    },
    'items.units.pc': {
        pt: 'Peça',
        en: 'Piece',
        es: 'Pieza',
    },
    'items.units.cx': {
        pt: 'Caixa',
        en: 'Box',
        es: 'Caja',
    },
    'items.units.pt': {
        pt: 'Pacote',
        en: 'Package',
        es: 'Paquete',
    },
    'items.units.sc': {
        pt: 'Saco',
        en: 'Bag',
        es: 'Bolsa',
    },

    // Items - Options
    'items.options.type.product': {
        pt: 'Material',
        en: 'Product',
        es: 'Producto',
    },
    'items.options.type.service': {
        pt: 'Serviço',
        en: 'Service',
        es: 'Servicio',
    },

    // Items - List
    'items.list.stats.totalItems': {
        pt: 'Total de itens',
        en: 'Total items',
        es: 'Total de ítems',
    },
    'items.list.stats.activeItems': {
        pt: 'Itens ativos',
        en: 'Active items',
        es: 'Ítems activos',
    },
    'items.list.search': {
        pt: 'Buscar itens...',
        en: 'Search items...',
        es: 'Buscar ítems...',
    },
    'items.list.filters.categoryId': {
        pt: 'Categoria (ID)',
        en: 'Category (ID)',
        es: 'Categoría (ID)',
    },
    'items.list.filters.tenderId': {
        pt: 'Pregão (ID)',
        en: 'Tender (ID)',
        es: 'Licitación (ID)',
    },
    'items.list.filters.lotId': {
        pt: 'Lote (ID)',
        en: 'Lot (ID)',
        es: 'Lote (ID)',
    },
    'items.list.filters.status': {
        pt: 'Status',
        en: 'Status',
        es: 'Estado',
    },
    'items.list.filters.all': {
        pt: 'Todos',
        en: 'All',
        es: 'Todos',
    },
    'items.list.filters.active': {
        pt: 'Ativos',
        en: 'Active',
        es: 'Activos',
    },
    'items.list.filters.inactive': {
        pt: 'Inativos',
        en: 'Inactive',
        es: 'Inactivos',
    },
    'items.list.headers.item': {
        pt: 'Item',
        en: 'Item',
        es: 'Ítem',
    },
    'items.list.headers.category': {
        pt: 'Categoria',
        en: 'Category',
        es: 'Categoría',
    },
    'items.list.headers.quantity': {
        pt: 'Quantidade',
        en: 'Quantity',
        es: 'Cantidad',
    },
    'items.list.headers.unitValue': {
        pt: 'Valor unitário',
        en: 'Unit value',
        es: 'Valor unitario',
    },
    'items.list.headers.totalValue': {
        pt: 'Valor total',
        en: 'Total value',
        es: 'Valor total',
    },
    'items.list.headers.status': {
        pt: 'Status',
        en: 'Status',
        es: 'Estado',
    },
    'items.list.headers.actions': {
        pt: 'Ações',
        en: 'Actions',
        es: 'Acciones',
    },
    'items.list.status.active': {
        pt: 'Ativo',
        en: 'Active',
        es: 'Activo',
    },
    'items.list.status.inactive': {
        pt: 'Inativo',
        en: 'Inactive',
        es: 'Inactivo',
    },

    // ==========================================
    // Categories - Public Tender
    // ==========================================
    'publicTenderCategories.singular': {
        pt: 'Categoria de Licitação',
        en: 'Tender Category',
        es: 'Categoría de Licitación',
    },
    'publicTenderCategories.plural': {
        pt: 'Categorias de Licitações',
        en: 'Tender Categories',
        es: 'Categorías de Licitaciones',
    },
    'publicTenderCategories.sections.info': {
        pt: 'Informações da categoria',
        en: 'Category information',
        es: 'Información de la categoría',
    },
    'publicTenderCategories.fields.code': {
        pt: 'Código',
        en: 'Code',
        es: 'Código',
    },
    'publicTenderCategories.fields.suggestedModality': {
        pt: 'Modalidade Sugerida',
        en: 'Suggested Modality',
        es: 'Modalidad Sugerida',
    },
    'publicTenderCategories.fields.minPublicationDays': {
        pt: 'Dias Mínimos de Publicação',
        en: 'Minimum Publication Days',
        es: 'Días Mínimos de Publicación',
    },
    'publicTenderCategories.fields.minSubmissionDays': {
        pt: 'Dias Mínimos de Submissão',
        en: 'Minimum Submission Days',
        es: 'Días Mínimos de Presentación',
    },
    'publicTenderCategories.fields.parentCategoryId': {
        pt: 'Categoria Pai',
        en: 'Parent Category',
        es: 'Categoría Padre',
    },
    'publicTenderCategories.fields.workflowTemplate': {
        pt: 'Template de Workflow',
        en: 'Workflow Template',
        es: 'Plantilla de Flujo de Trabajo',
    },
    'publicTenderCategories.fields.requiredDocumentation': {
        pt: 'Documentação Obrigatória',
        en: 'Required Documentation',
        es: 'Documentación Requerida',
    },
    'publicTenderCategories.fields.commissionProfile': {
        pt: 'Perfil da Comissão',
        en: 'Commission Profile',
        es: 'Perfil de la Comisión',
    },
    'publicTenderCategories.fields.complianceRules': {
        pt: 'Regras de Compliance',
        en: 'Compliance Rules',
        es: 'Reglas de Cumplimiento',
    },
    'publicTenderCategories.fields.successMetrics': {
        pt: 'Métricas de Sucesso',
        en: 'Success Metrics',
        es: 'Métricas de Éxito',
    },

    // ==========================================
    // Categories - Lots
    // ==========================================
    'lotCategories.singular': {
        pt: 'Categoria de Lote',
        en: 'Lot Category',
        es: 'Categoría de Lote',
    },
    'lotCategories.plural': {
        pt: 'Categorias de Lotes',
        en: 'Lot Categories',
        es: 'Categorías de Lotes',
    },
    'lotCategories.sections.basic': {
        pt: 'Informações básicas',
        en: 'Basic information',
        es: 'Información básica',
    },
    'lotCategories.fields.code': {
        pt: 'Código',
        en: 'Code',
        es: 'Código',
    },
    'lotCategories.fields.groupingStrategy': {
        pt: 'Estratégia de Agrupamento',
        en: 'Grouping Strategy',
        es: 'Estrategia de Agrupación',
    },
    'lotCategories.fields.minEstimatedValue': {
        pt: 'Valor Estimado Mínimo',
        en: 'Minimum Estimated Value',
        es: 'Valor Estimado Mínimo',
    },
    'lotCategories.fields.maxEstimatedValue': {
        pt: 'Valor Estimado Máximo',
        en: 'Maximum Estimated Value',
        es: 'Valor Estimado Máximo',
    },
    'lotCategories.fields.recommendedME_EPPQuota': {
        pt: 'Quota Recomendada ME/EPP',
        en: 'Recommended ME/EPP Quota',
        es: 'Cuota Recomendada ME/EPP',
    },
    'lotCategories.fields.complexityLevel': {
        pt: 'Nível de Complexidade',
        en: 'Complexity Level',
        es: 'Nivel de Complejidad',
    },
    'lotCategories.fields.riskModel': {
        pt: 'Modelo de Risco',
        en: 'Risk Model',
        es: 'Modelo de Riesgo',
    },
    'lotCategories.fields.judgmentCriteria': {
        pt: 'Critério de Julgamento',
        en: 'Judgment Criteria',
        es: 'Criterio de Juicio',
    },
    'lotCategories.fields.technicalRequirements': {
        pt: 'Requisitos Técnicos',
        en: 'Technical Requirements',
        es: 'Requisitos Técnicos',
    },

    // ==========================================
    // Categories - Items
    // ==========================================
    'itemCategories.singular': {
        pt: 'Categoria de Item',
        en: 'Item Category',
        es: 'Categoría de Ítem',
    },
    'itemCategories.plural': {
        pt: 'Categorias de Itens',
        en: 'Item Categories',
        es: 'Categorías de Ítems',
    },
    'itemCategories.fields.code': {
        pt: 'Código',
        en: 'Code',
        es: 'Código',
    },
    'itemCategories.fields.parentCategoryId': {
        pt: 'Categoria Pai',
        en: 'Parent Category',
        es: 'Categoría Padre',
    },
    'itemCategories.fields.catmatCategory': {
        pt: 'Categoria CATMAT',
        en: 'CATMAT Category',
        es: 'Categoría CATMAT',
    },
    'itemCategories.fields.catserCategory': {
        pt: 'Categoria CATSER',
        en: 'CATSER Category',
        es: 'Categoría CATSER',
    },
    'itemCategories.fields.technicalRequirements': {
        pt: 'Requisitos Técnicos',
        en: 'Technical Requirements',
        es: 'Requisitos Técnicos',
    },
    'itemCategories.fields.sustainabilityCriteria': {
        pt: 'Critérios de Sustentabilidade',
        en: 'Sustainability Criteria',
        es: 'Criterios de Sostenibilidad',
    },
    'itemCategories.fields.requiredCertifications': {
        pt: 'Certificações Obrigatórias',
        en: 'Required Certifications',
        es: 'Certificaciones Requeridas',
    },
    'itemCategories.fields.specificationTemplate': {
        pt: 'Template de Especificação',
        en: 'Specification Template',
        es: 'Plantilla de Especificación',
    },
    // Vinculos - Proposals section
    'vinculos.proposals.title': {
        pt: 'Propostas vinculadas à esta Licitação',
        en: 'Proposals linked to this Tender',
        es: 'Propuestas vinculadas a esta Licitación',
    },
    'vinculos.proposals.open': {
        pt: 'Abrir propostas',
        en: 'Open proposals',
        es: 'Abrir propuestas',
    },
    'vinculos.proposals.loading': {
        pt: 'Carregando propostas...',
        en: 'Loading proposals...',
        es: 'Cargando propuestas...',
    },
    'vinculos.proposals.empty': {
        pt: 'Nenhuma proposta encontrada para esta licitação.',
        en: 'No proposals found for this tender.',
        es: 'No se encontraron propuestas para esta licitación.',
    },
    'vinculos.proposals.loadError': {
        pt: 'Erro ao carregar propostas',
        en: 'Error loading proposals',
        es: 'Error al cargar propuestas',
    },
    'vinculos.proposals.proposalNumber': {
        pt: 'N° Proposta',
        en: 'Proposal #',
        es: 'N° Propuesta',
    },
    'vinculos.proposals.supplier': {
        pt: 'Fornecedor',
        en: 'Supplier',
        es: 'Proveedor',
    },
    'vinculos.proposals.value': {
        pt: 'Valor',
        en: 'Value',
        es: 'Valor',
    },
    'vinculos.proposals.status': {
        pt: 'Status',
        en: 'Status',
        es: 'Estado',
    },
    'vinculos.proposals.ranking': {
        pt: 'Ranking',
        en: 'Ranking',
        es: 'Ranking',
    },
    'vinculos.proposals.submittedAt': {
        pt: 'Enviada em',
        en: 'Submitted at',
        es: 'Enviada en',
    },
    'vinculos.proposals.actions': {
        pt: 'Ações',
        en: 'Actions',
        es: 'Acciones',
    },
    'vinculos.proposals.view': {
        pt: 'Ver',
        en: 'View',
        es: 'Ver',
    },
    'vinculos.proposalStatus.draft': {
        pt: 'Rascunho',
        en: 'Draft',
        es: 'Borrador',
    },
    'vinculos.proposalStatus.submitted': {
        pt: 'Enviada',
        en: 'Submitted',
        es: 'Enviada',
    },
    'vinculos.proposalStatus.accepted': {
        pt: 'Aceita',
        en: 'Accepted',
        es: 'Aceptada',
    },
    'vinculos.proposalStatus.rejected': {
        pt: 'Rejeitada',
        en: 'Rejected',
        es: 'Rechazada',
    },
    'vinculos.proposalStatus.winner': {
        pt: 'Vencedora',
        en: 'Winner',
        es: 'Ganadora',
    },
    // Lots & Items section
    'lotsAndItems.title': {
        pt: 'Lotes vinculados à esta Licitação',
        en: 'Lots linked to this Tender',
        es: 'Lotes vinculados a esta Licitación',
    },
    'lotsAndItems.addLot': {
        pt: 'Adicionar lote',
        en: 'New lot',
        es: 'Nuevo lote',
    },
    // Trava Flavio 23/07: composicao imutavel com edital publicado
    'lotsAndItems.compositionLocked': {
        pt: 'Edital publicado — lotes e itens não podem ser alterados. Para alterar a composição, suspenda a licitação, faça as alterações e retome (republique).',
        en: 'Notice published — lots and items cannot be changed. To change the composition, suspend the tender, make the changes and resume (republish).',
        es: 'Edicto publicado — los lotes y artículos no se pueden modificar. Para cambiar la composición, suspenda la licitación, realice los cambios y reanude (republique).',
    },
    'lotsAndItems.loadingLots': {
        pt: 'Carregando lotes...',
        en: 'Loading lots...',
        es: 'Cargando lotes...',
    },
    'lotsAndItems.noLots': {
        pt: 'Nenhum lote encontrado.',
        en: 'No lots found.',
        es: 'No se encontraron lotes.',
    },
    'lotsAndItems.noTitle': {
        pt: 'Sem título',
        en: 'No title',
        es: 'Sin título',
    },
    'lotsAndItems.productBadge': {
        pt: 'Produto',
        en: 'Product',
        es: 'Producto',
    },
    'lotsAndItems.totalValue': {
        pt: 'Valor total:',
        en: 'Total value:',
        es: 'Valor total:',
    },
    'lotsAndItems.meEppQuota': {
        pt: 'ME/EPP:',
        en: 'ME/EPP:',
        es: 'ME/EPP:',
    },
    'lotsAndItems.addItem': {
        pt: 'Adicionar item',
        en: 'Add item',
        es: 'Agregar ítem',
    },
    'lotsAndItems.quickEdit': {
        pt: 'Edição rápida',
        en: 'Quick edit',
        es: 'Edición rápida',
    },
    // Exclusão de lote/item (Pato Branco, 15/10): cadastraram item duplicado e
    // não havia caminho na tela — a única saída era mexer no banco.
    'lotsAndItems.delete': {
        pt: 'Excluir',
        en: 'Delete',
        es: 'Eliminar',
    },
    'lotsAndItems.deleteItemTitle': {
        pt: 'Excluir item',
        en: 'Delete item',
        es: 'Eliminar ítem',
    },
    'lotsAndItems.deleteLotTitle': {
        pt: 'Excluir lote',
        en: 'Delete lot',
        es: 'Eliminar lote',
    },
    'lotsAndItems.deleteWarning': {
        pt: 'Esta ação não pode ser desfeita por esta tela.',
        en: 'This action cannot be undone from this screen.',
        es: 'Esta acción no se puede deshacer desde esta pantalla.',
    },
    'lotsAndItems.deleteCancel': {
        pt: 'Cancelar',
        en: 'Cancel',
        es: 'Cancelar',
    },
    'lotsAndItems.expandLot': {
        pt: 'Expandir lote',
        en: 'Expand lot',
        es: 'Expandir lote',
    },
    'lotsAndItems.collapseLot': {
        pt: 'Recolher lote',
        en: 'Collapse lot',
        es: 'Contraer lote',
    },
    'lotsAndItems.lotItems': {
        pt: 'Itens do lote',
        en: 'Lot items',
        es: 'Ítems del lote',
    },
    'lotsAndItems.loadingItems': {
        pt: 'Carregando itens...',
        en: 'Loading items...',
        es: 'Cargando ítems...',
    },
    'lotsAndItems.listedItems': {
        pt: 'Itens listados:',
        en: 'Listed items:',
        es: 'Ítems listados:',
    },
    'lotsAndItems.listedItemsOf': {
        pt: 'de',
        en: 'of',
        es: 'de',
    },
    'lotsAndItems.filterPlaceholder': {
        pt: 'Filtrar descrição ou código',
        en: 'Filter description or code',
        es: 'Filtrar descripción o código',
    },
    'lotsAndItems.clearFilter': {
        pt: 'Limpar',
        en: 'Clear',
        es: 'Limpiar',
    },
    'lotsAndItems.colDescription': {
        pt: 'Descrição',
        en: 'Description',
        es: 'Descripción',
    },
    'lotsAndItems.colUnit': {
        pt: 'Unidade',
        en: 'Unit',
        es: 'Unidad',
    },
    'lotsAndItems.colQuantity': {
        pt: 'Quantidade',
        en: 'Quantity',
        es: 'Cantidad',
    },
    'lotsAndItems.colUnitValue': {
        pt: 'Valor Unitário',
        en: 'Unit Value',
        es: 'Valor Unitario',
    },
    'lotsAndItems.colTotalValue': {
        pt: 'Valor Total',
        en: 'Total Value',
        es: 'Valor Total',
    },
    'lotsAndItems.colActions': {
        pt: 'Ações',
        en: 'Actions',
        es: 'Acciones',
    },
    'lotsAndItems.noItemsFiltered': {
        pt: 'Nenhum item encontrado com o filtro aplicado',
        en: 'No items found matching the filter',
        es: 'No se encontraron ítems con el filtro aplicado',
    },
    'lotsAndItems.noItemsInLot': {
        pt: 'Nenhum item cadastrado para este lote',
        en: 'No items registered for this lot',
        es: 'No hay ítems registrados para este lote',
    },

    // ==========================================
    // Options - Tender Status (for form dropdowns)
    // ==========================================
    'options.tenderStatus.rascunho': { pt: 'Rascunho', en: 'Draft', es: 'Borrador' },
    'options.tenderStatus.publicado': { pt: 'Publicado', en: 'Published', es: 'Publicado' },
    'options.tenderStatus.recebendoPropostas': { pt: 'Recebendo propostas', en: 'Receiving proposals', es: 'Recibiendo propuestas' },
    'options.tenderStatus.analisandoPropostas': { pt: 'Analisando propostas', en: 'Analyzing proposals', es: 'Analizando propuestas' },
    'options.tenderStatus.emDisputa': { pt: 'Em disputa', en: 'In dispute', es: 'En disputa' },
    'options.tenderStatus.habilitacao': { pt: 'Habilitação', en: 'Qualification', es: 'Habilitación' },
    'options.tenderStatus.adjudicado': { pt: 'Adjudicado', en: 'Awarded', es: 'Adjudicado' },
    'options.tenderStatus.homologado': { pt: 'Homologado', en: 'Ratified', es: 'Homologado' },
    'options.tenderStatus.cancelado': { pt: 'Cancelado', en: 'Cancelled', es: 'Cancelado' },
    'options.tenderStatus.fracassado': { pt: 'Fracassado', en: 'Failed', es: 'Fracasado' },
    'options.tenderStatus.suspenso': { pt: 'Suspenso', en: 'Suspended', es: 'Suspendido' },
    'options.tenderStatus.revogado': { pt: 'Revogado', en: 'Revoked', es: 'Revocado' },
    'options.tenderStatus.anulado': { pt: 'Anulado', en: 'Annulled', es: 'Anulado' },
    'options.tenderStatus.emJulgamento': { pt: 'Em julgamento', en: 'Under judgment', es: 'En juicio' },
    'options.tenderStatus.emRecurso': { pt: 'Em recurso', en: 'In appeal', es: 'En recurso' },
    'options.tenderStatus.adjudicando': { pt: 'Adjudicando', en: 'Awarding', es: 'Adjudicando' },
    'options.tenderStatus.emRetificacao': { pt: 'Em retificação', en: 'Under rectification', es: 'En rectificación' },
    'options.tenderStatus.resultadoFinal': { pt: 'Resultado final', en: 'Final result', es: 'Resultado final' },
    'options.tenderStatus.deserta': { pt: 'Deserta', en: 'Deserted', es: 'Desierta' },
    'options.tenderStatus.DRAFT': { pt: 'Rascunho', en: 'Draft', es: 'Borrador' },
    'options.tenderStatus.PUBLISHED': { pt: 'Publicado', en: 'Published', es: 'Publicado' },
    'options.tenderStatus.RECEIVING_PROPOSALS': { pt: 'Recebendo propostas', en: 'Receiving proposals', es: 'Recibiendo propuestas' },
    'options.tenderStatus.ANALYZING_PROPOSALS': { pt: 'Analisando propostas', en: 'Analyzing proposals', es: 'Analizando propuestas' },
    'options.tenderStatus.IN_DISPUTE': { pt: 'Em disputa', en: 'In dispute', es: 'En disputa' },
    'options.tenderStatus.IN_ENABLEMENT': { pt: 'Habilitação', en: 'Qualification', es: 'Habilitación' },
    'options.tenderStatus.IN_JUDGMENT': { pt: 'Em julgamento', en: 'Under judgment', es: 'En juicio' },
    'options.tenderStatus.IN_APPEAL': { pt: 'Em recurso', en: 'In appeal', es: 'En recurso' },
    'options.tenderStatus.AWARDING': { pt: 'Adjudicando', en: 'Awarding', es: 'Adjudicando' },
    'options.tenderStatus.AWARDED': { pt: 'Adjudicado', en: 'Awarded', es: 'Adjudicado' },
    'options.tenderStatus.HOMOLOGATED': { pt: 'Homologado', en: 'Ratified', es: 'Homologado' },
    'options.tenderStatus.FINAL_RESULT': { pt: 'Resultado final', en: 'Final result', es: 'Resultado final' },
    'options.tenderStatus.DESERTED': { pt: 'Deserta', en: 'Deserted', es: 'Desierta' },
    'options.tenderStatus.CANCELLED': { pt: 'Cancelado', en: 'Cancelled', es: 'Cancelado' },
    'options.tenderStatus.FAILED': { pt: 'Fracassado', en: 'Failed', es: 'Fracasado' },
    'options.tenderStatus.SUSPENDED': { pt: 'Suspenso', en: 'Suspended', es: 'Suspendido' },
    'options.tenderStatus.REVOKED': { pt: 'Revogado', en: 'Revoked', es: 'Revocado' },
    'options.tenderStatus.ANNULLED': { pt: 'Anulado', en: 'Annulled', es: 'Anulado' },
    'options.tenderStatus.UNDER_RECTIFICATION': { pt: 'Em retificação', en: 'Under rectification', es: 'En rectificación' },

    // ==========================================
    // Options - Contract Types
    // ==========================================
    'options.contractType.aquisicao': { pt: 'Aquisição', en: 'Acquisition', es: 'Adquisición' },
    'options.contractType.registroDePreco': { pt: 'Registro de preço', en: 'Price registration', es: 'Registro de precios' },
    'options.contractType.aquisicaoParcelada': { pt: 'Aquisição parcelada', en: 'Installment acquisition', es: 'Adquisición parcelada' },
    'options.contractType.prestacaoDeServico': { pt: 'Prestação de serviço', en: 'Service provision', es: 'Prestación de servicio' },
    'options.contractType.ACQUISITION': { pt: 'Aquisição', en: 'Acquisition', es: 'Adquisición' },
    'options.contractType.PRICE_REGISTRY': { pt: 'Registro de preço', en: 'Price registry', es: 'Registro de precios' },
    'options.contractType.PARTIAL_ACQUISITION': { pt: 'Aquisição parcelada', en: 'Partial acquisition', es: 'Adquisición parcelada' },
    'options.contractType.SERVICE_PROVISION': { pt: 'Prestação de serviço', en: 'Service provision', es: 'Prestación de servicio' },

    // ==========================================
    // Options - Funding Sources
    // ==========================================
    'options.fundingSource.proprio': { pt: 'Próprio', en: 'Own funds', es: 'Fondos propios' },
    'options.fundingSource.convenio': { pt: 'Convênio', en: 'Agreement', es: 'Convenio' },
    'options.fundingSource.estadual': { pt: 'Estadual', en: 'State', es: 'Estatal' },
    'options.fundingSource.federal': { pt: 'Federal', en: 'Federal', es: 'Federal' },
    'options.fundingSource.OWN': { pt: 'Próprio', en: 'Own funds', es: 'Fondos propios' },
    'options.fundingSource.AGREEMENT': { pt: 'Convênio', en: 'Agreement', es: 'Convenio' },
    'options.fundingSource.STATE': { pt: 'Estadual', en: 'State', es: 'Estatal' },
    'options.fundingSource.FEDERAL': { pt: 'Federal', en: 'Federal', es: 'Federal' },

    // ==========================================
    // Options - Modalities (form dropdowns)
    // ==========================================
    'options.modality.pregaoEletronico': { pt: 'Pregão eletrônico', en: 'Electronic bidding', es: 'Subasta electrónica' },
    'options.modality.pregaoPresencial': { pt: 'Pregão presencial', en: 'In-person bidding', es: 'Subasta presencial' },
    'options.modality.disputaEletronica': { pt: 'Dispensa Eletrônica', en: 'Electronic waiver', es: 'Dispensa electrónica' },
    'options.modality.concorrenciaEletronica': { pt: 'Concorrência eletrônica', en: 'Electronic competition', es: 'Concurrencia electrónica' },
    'options.modality.leilaoEletronico': { pt: 'Leilão eletrônico', en: 'Electronic auction', es: 'Subasta electrónica' },
    'options.modality.credenciamento': { pt: 'Credenciamento', en: 'Accreditation', es: 'Acreditación' },
    'options.modality.dialogoCompetitivo': { pt: 'Diálogo Competitivo', en: 'Competitive dialogue', es: 'Diálogo competitivo' },
    'options.modality.inexigibilidadeEletronica': { pt: 'Inexigibilidade Eletrônica', en: 'Electronic unenforceability', es: 'Inexigibilidad electrónica' },
    'options.modality.ELECTRONIC_AUCTION': { pt: 'Pregão eletrônico', en: 'Electronic auction', es: 'Subasta electrónica' },
    'options.modality.PRESENTIAL_AUCTION': { pt: 'Pregão presencial', en: 'In-person auction', es: 'Subasta presencial' },
    'options.modality.ELECTRONIC_DISPUTE': { pt: 'Disputa eletrônica', en: 'Electronic dispute', es: 'Disputa electrónica' },
    'options.modality.ELECTRONIC_COMPETITION': { pt: 'Concorrência eletrônica', en: 'Electronic competition', es: 'Concurrencia electrónica' },
    'options.modality.ELECTRONIC_BIDDING': { pt: 'Leilão eletrônico', en: 'Electronic bidding', es: 'Subasta electrónica' },

    // ==========================================
    // Options - Judgement Criteria
    // ==========================================
    'options.judgementCriteria.menorPreco': { pt: 'Menor Preço', en: 'Lowest Price', es: 'Menor Precio' },
    'options.judgementCriteria.melhorTecnica': { pt: 'Melhor Técnica', en: 'Best Technique', es: 'Mejor Técnica' },
    'options.judgementCriteria.tecnicaEPreco': { pt: 'Técnica e Preço', en: 'Technique and Price', es: 'Técnica y Precio' },
    'options.judgementCriteria.maiorDesconto': { pt: 'Maior Desconto', en: 'Greatest Discount', es: 'Mayor Descuento' },
    'options.judgementCriteria.maiorRetornoEconomico': { pt: 'Maior Retorno Econômico', en: 'Greatest Economic Return', es: 'Mayor Retorno Económico' },
    'options.judgementCriteria.maiorLance': { pt: 'Maior Lance', en: 'Highest Bid', es: 'Mayor Oferta' },
    'options.judgementCriteria.menorLance': { pt: 'Menor Lance', en: 'Lowest Bid', es: 'Menor Oferta' },
    'options.judgementCriteria.concurso': { pt: 'Concurso', en: 'Contest', es: 'Concurso' },
    'options.judgementCriteria.melhorTecnicaOuConteudoArtistico': { pt: 'Melhor Técnica ou Conteúdo Artístico', en: 'Best Technique or Artistic Content', es: 'Mejor Técnica o Contenido Artístico' },
    'options.judgementCriteria.melhorTecnicaEPreco': { pt: 'Melhor Técnica e Preço', en: 'Best Technique and Price', es: 'Mejor Técnica y Precio' },
    'options.judgementCriteria.LOWEST_PRICE': { pt: 'Menor Preço', en: 'Lowest Price', es: 'Menor Precio' },
    'options.judgementCriteria.LOWEST_BID': { pt: 'Menor Lance', en: 'Lowest Bid', es: 'Menor Oferta' },
    'options.judgementCriteria.BEST_TECHNICAL': { pt: 'Melhor Técnica', en: 'Best Technique', es: 'Mejor Técnica' },
    'options.judgementCriteria.TECHNIQUE_AND_PRICE': { pt: 'Técnica e Preço', en: 'Technique and Price', es: 'Técnica y Precio' },
    'options.judgementCriteria.BEST_TECHNICAL_AND_PRICE': { pt: 'Melhor Técnica e Preço', en: 'Best Technique and Price', es: 'Mejor Técnica y Precio' },
    'options.judgementCriteria.HIGHEST_DISCOUNT': { pt: 'Maior Desconto', en: 'Highest Discount', es: 'Mayor Descuento' },
    'options.judgementCriteria.HIGHEST_ECONOMIC_RETURN': { pt: 'Maior Retorno Econômico', en: 'Highest Economic Return', es: 'Mayor Retorno Económico' },
    'options.judgementCriteria.HIGHEST_BID': { pt: 'Maior Lance', en: 'Highest Bid', es: 'Mayor Oferta' },
    'options.judgementCriteria.CONTEST': { pt: 'Concurso', en: 'Contest', es: 'Concurso' },
    'options.judgementCriteria.BEST_ARTISTIC_CONTENT': { pt: 'Melhor Técnica ou Conteúdo Artístico', en: 'Best Technique or Artistic Content', es: 'Mejor Técnica o Contenido Artístico' },

    // ==========================================
    // Options - Dispute Types
    // ==========================================
    'options.disputeType.aberto': { pt: 'Aberto', en: 'Open', es: 'Abierto' },
    'options.disputeType.abertoFechado': { pt: 'Aberto-fechado', en: 'Open-closed', es: 'Abierto-cerrado' },
    'options.disputeType.fechado': { pt: 'Fechado', en: 'Closed', es: 'Cerrado' },
    'options.disputeType.fechadoAberto': { pt: 'Fechado-aberto', en: 'Closed-open', es: 'Cerrado-abierto' },
    'options.disputeType.OPEN': { pt: 'Aberto', en: 'Open', es: 'Abierto' },
    'options.disputeType.OPEN_THEN_CLOSED': { pt: 'Aberto-fechado', en: 'Open-closed', es: 'Abierto-cerrado' },
    'options.disputeType.CLOSED': { pt: 'Fechado', en: 'Closed', es: 'Cerrado' },
    'options.disputeType.CLOSED_THEN_OPEN': { pt: 'Fechado-aberto', en: 'Closed-open', es: 'Cerrado-abierto' },

    // ==========================================
    // Options - Supplier Regionality Benefits
    // ==========================================
    'options.supplierRegionality.herdar': { pt: 'Usar configuração geral da licitação', en: 'Use general tender settings', es: 'Usar configuración general de la licitación' },
    'options.supplierRegionality.nao': { pt: 'Sem Benefício', en: 'No Benefit', es: 'Sin Beneficio' },
    'options.supplierRegionality.preferenciaEstadual': { pt: 'Preferência Estadual', en: 'State Preference', es: 'Preferencia Estatal' },
    'options.supplierRegionality.exclusivoEstadual': { pt: 'Exclusivo Estadual', en: 'State Exclusive', es: 'Exclusivo Estatal' },
    'options.supplierRegionality.preferenciaMunicipal': { pt: 'Preferência Municipal', en: 'Municipal Preference', es: 'Preferencia Municipal' },
    'options.supplierRegionality.exclusivoMunicipal': { pt: 'Exclusivo Municipal', en: 'Municipal Exclusive', es: 'Exclusivo Municipal' },

    // ==========================================
    // Options - Supplier Size Benefits
    // ==========================================
    'options.supplierSize.herdar': { pt: 'Usar configuração geral da licitação', en: 'Use general tender settings', es: 'Usar configuración general de la licitación' },
    'options.supplierSize.nao': { pt: 'Sem Benefício', en: 'No Benefit', es: 'Sin Beneficio' },
    'options.supplierSize.preferencia': { pt: 'Preferência ME/EPP/COOP', en: 'ME/EPP/COOP Preference', es: 'Preferencia ME/EPP/COOP' },
    'options.supplierSize.exclusivo': { pt: 'Exclusivo ME/EPP/COOP', en: 'ME/EPP/COOP Exclusive', es: 'Exclusivo ME/EPP/COOP' },

    // ==========================================
    // List - View Labels
    // ==========================================
    'publicTenders.list.supplierView': { pt: 'Visão Fornecedor', en: 'Supplier View', es: 'Vista Proveedor' },
    'publicTenders.list.auctioneerView': { pt: 'Visão Pregoeiro', en: 'Auctioneer View', es: 'Vista Pregonero' },

    // ==========================================
    // Compliance Fields (Lei 14.133)
    // ==========================================

    // Public Tenders - Art. 12 VII (PCA)
    'publicTenders.fields.pcaReference': { pt: 'Referência PCA', en: 'PCA Reference', es: 'Referencia PCA' },
    'publicTenders.fields.pcaReference.hint': { pt: 'Código do item no Plano de Contratações Anual (opcional)', en: 'Annual Procurement Plan item code (optional)', es: 'Código del Plan de Contrataciones Anual (opcional)' },

    // Items - Art. 40 §1° (Brand), Art. 41 (Samples), Art. 42 (Quality)
    'publicTenders.items.fields.brandJustification': { pt: 'Justificativa de Marca', en: 'Brand Justification', es: 'Justificación de Marca' },
    'publicTenders.items.fields.brandJustification.hint': { pt: 'Obrigatório se marca indicada (Art. 40 §1°)', en: 'Required if brand specified (Art. 40 §1°)', es: 'Obligatorio si marca indicada (Art. 40 §1°)' },
    'publicTenders.items.fields.requiresSample': { pt: 'Exige Amostra', en: 'Requires Sample', es: 'Requiere Muestra' },
    'publicTenders.items.fields.requiresProofOfConcept': { pt: 'Exige Prova de Conceito', en: 'Requires Proof of Concept', es: 'Requiere Prueba de Concepto' },
    'publicTenders.items.fields.qualityStandards': { pt: 'Normas de Qualidade', en: 'Quality Standards', es: 'Normas de Calidad' },
    'publicTenders.items.fields.qualityStandards.hint': { pt: 'ABNT, Inmetro, certificações aplicáveis (Art. 42)', en: 'ABNT, Inmetro, applicable certifications (Art. 42)', es: 'ABNT, Inmetro, certificaciones aplicables (Art. 42)' },
    'publicTenders.items.fields.requiresCertification': { pt: 'Exige Certificação', en: 'Requires Certification', es: 'Requiere Certificación' },

    // Lots - Art. 47 §1° (Grouping), Art. 40 §5° (ME/EPP)
    'publicTenders.lots.fields.lotGroupingJustification': { pt: 'Justificativa de Agrupamento', en: 'Grouping Justification', es: 'Justificación de Agrupamiento' },
    'publicTenders.lots.fields.lotGroupingJustification.hint': { pt: 'Obrigatório se lote com mais de 1 item (Art. 47 §1°)', en: 'Required if lot has more than 1 item (Art. 47 §1°)', es: 'Obligatorio si lote con más de 1 ítem (Art. 47 §1°)' },
    'publicTenders.lots.fields.meEppBenefitType': { pt: 'Benefício ME/EPP', en: 'SME/EPP Benefit', es: 'Beneficio ME/EPP' },
    'publicTenders.lots.sections.compliance': { pt: 'Conformidade Legal', en: 'Legal Compliance', es: 'Conformidad Legal' },

    // Lots - Art. 48 II/III LC 123/2006 — subcontratação ME/EPP + cota 25%
    'publicTenders.lots.sections.meeppSubcontracting': {
        pt: 'Subcontratação ME/EPP — Art. 48 II LC 123/2006',
        en: 'SME/EPP Subcontracting — Art. 48 II LC 123/2006',
        es: 'Subcontratación ME/EPP — Art. 48 II LC 123/2006',
    },
    'publicTenders.lots.sections.meeppReservedQuota': {
        pt: 'Cota reservada ME/EPP — Art. 48 III LC 123/2006 (até 25%)',
        en: 'SME/EPP reserved quota — Art. 48 III LC 123/2006 (up to 25%)',
        es: 'Cuota reservada ME/EPP — Art. 48 III LC 123/2006 (hasta 25%)',
    },
    'publicTenders.lots.fields.requiresMeEppSubcontracting': {
        pt: 'Exige subcontratação de ME/EPP',
        en: 'Requires SME/EPP subcontracting',
        es: 'Exige subcontratación de ME/EPP',
    },
    'publicTenders.lots.fields.meEppSubcontractingPercentage': {
        pt: 'Percentual mínimo de subcontratação (10–30%)',
        en: 'Minimum subcontracting percentage (10–30%)',
        es: 'Porcentaje mínimo de subcontratación (10–30%)',
    },
    'publicTenders.lots.fields.meEppSubcontractingPercentage.hint': {
        pt: 'Faixa permitida pela lei: 10% a 30%.',
        en: 'Legally allowed range: 10% to 30%.',
        es: 'Rango permitido por ley: 10% al 30%.',
    },
    'publicTenders.lots.fields.meEppReservedQuotaPercentage': {
        pt: 'Cota reservada (0–25%)',
        en: 'Reserved quota (0–25%)',
        es: 'Cuota reservada (0–25%)',
    },
    'publicTenders.lots.fields.meEppQuotaWaived': {
        pt: 'Dispensar cota (com justificativa formal)',
        en: 'Waive quota (with formal justification)',
        es: 'Dispensar cuota (con justificación formal)',
    },
    'publicTenders.lots.fields.meEppQuotaWaiverReason': {
        pt: 'Motivo da dispensa',
        en: 'Waiver reason',
        es: 'Motivo de la dispensa',
    },
    'publicTenders.lots.fields.meEppQuotaWaiverReason.options.INSUFFICIENT_MEEPP_SUPPLIERS': {
        pt: 'Fornecedores ME/EPP insuficientes na região',
        en: 'Insufficient SME/EPP suppliers in the region',
        es: 'Proveedores ME/EPP insuficientes en la región',
    },
    'publicTenders.lots.fields.meEppQuotaWaiverReason.options.INDIVISIBLE_GOOD': {
        pt: 'Bem/serviço indivisível',
        en: 'Indivisible good/service',
        es: 'Bien/servicio indivisible',
    },
    'publicTenders.lots.fields.meEppQuotaWaiverReason.options.NO_COMPETITIVE_PRICE_DISADVANTAGE': {
        pt: 'Preço não competitivo em ME/EPP',
        en: 'Non-competitive price for SME/EPP',
        es: 'Precio no competitivo en ME/EPP',
    },
    'publicTenders.lots.fields.meEppQuotaWaiverReason.options.OTHER': {
        pt: 'Outro motivo (descrever)',
        en: 'Other reason (describe)',
        es: 'Otro motivo (describir)',
    },
    'publicTenders.lots.fields.meEppQuotaWaiverJustification': {
        pt: 'Justificativa detalhada da dispensa',
        en: 'Detailed waiver justification',
        es: 'Justificación detallada de la dispensa',
    },

    // Judgment — Art. 62 Lei 14.133/2021 (granular qualification criteria)
    'auction.judgment.art62.sectionTitle': {
        pt: 'Critérios de habilitação (Art. 62 Lei 14.133)',
        en: 'Qualification criteria (Art. 62 Law 14.133)',
        es: 'Criterios de habilitación (Art. 62 Ley 14.133)',
    },
    'auction.judgment.art62.hint': {
        pt: 'Backend valida coerência: "Declarar Vencedor" exige todos true. "Inabilitado" exige ≥1 false. "Parcialmente" aceita qualquer mix.',
        en: 'Backend validates coherence: "Declare Winner" requires all true. "Disqualified" requires ≥1 false. "Partially" accepts any mix.',
        es: 'Backend valida coherencia: "Declarar Ganador" exige todos true. "Inhabilitado" exige ≥1 false. "Parcialmente" acepta cualquier mix.',
    },
    'auction.judgment.art62.fiscalRegularity': {
        pt: 'Regularidade fiscal/social/trabalhista (IV)',
        en: 'Fiscal/social/labor regularity (IV)',
        es: 'Regularidad fiscal/social/laboral (IV)',
    },
    'auction.judgment.art62.technical': {
        pt: 'Qualificação técnica (II)',
        en: 'Technical qualification (II)',
        es: 'Calificación técnica (II)',
    },
    'auction.judgment.art62.economic': {
        pt: 'Qualificação econômico-financeira (III)',
        en: 'Economic-financial qualification (III)',
        es: 'Calificación económico-financiera (III)',
    },
    'auction.judgment.art62.minimumCapital': {
        pt: 'Capital mínimo (III — opcional)',
        en: 'Minimum capital (III — optional)',
        es: 'Capital mínimo (III — opcional)',
    },
    'auction.judgment.art62.minimumEquity': {
        pt: 'Patrimônio mínimo (III — opcional)',
        en: 'Minimum equity (III — optional)',
        es: 'Patrimonio mínimo (III — opcional)',
    },

    // Judgment — Solicitar novo lance (Art. 61 Lei 14.133 — negociacao 1:1)
    'auction.judgment.newBid.success': {
        pt: 'Novo lance solicitado a {supplier} no lote {lot}.',
        en: 'New bid requested from {supplier} on lot {lot}.',
        es: 'Nueva oferta solicitada a {supplier} en el lote {lot}.',
    },
    'auction.judgment.newBid.error': {
        pt: 'Não foi possível solicitar o novo lance.',
        en: 'Could not request the new bid.',
        es: 'No fue posible solicitar la nueva oferta.',
    },
    'auction.judgment.newBid.invalidDeadline': {
        pt: 'Informe uma data e hora limite no futuro.',
        en: 'Provide a deadline date and time in the future.',
        es: 'Informe una fecha y hora límite en el futuro.',
    },
    'auction.judgment.newBid.missingAward': {
        pt: 'Adjudicação não localizada para este fornecedor.',
        en: 'Award not found for this supplier.',
        es: 'Adjudicación no encontrada para este proveedor.',
    },
    'auction.judgment.newBid.submitting': {
        pt: 'Enviando…',
        en: 'Submitting…',
        es: 'Enviando…',
    },
    'auction.judgment.newBid.awaitingBadge': {
        pt: 'Aguardando novo lance — prazo {deadline}',
        en: 'Awaiting new bid — deadline {deadline}',
        es: 'Esperando nueva oferta — plazo {deadline}',
    },
    'auction.judgment.newBid.expiredBadge': {
        pt: 'Prazo de novo lance expirado em {deadline}',
        en: 'New-bid deadline expired on {deadline}',
        es: 'Plazo de nueva oferta expirado el {deadline}',
    },

    // Judgment — Solicitar documentos complementares (Art. 64 Lei 14.133 — diligência)
    'auction.judgment.requestDocs.success': {
        pt: '{count} documento(s) solicitado(s) a {supplier}.',
        en: '{count} document(s) requested from {supplier}.',
        es: '{count} documento(s) solicitado(s) a {supplier}.',
    },
    'auction.judgment.requestDocs.error': {
        pt: 'Não foi possível solicitar os documentos.',
        en: 'Could not request documents.',
        es: 'No fue posible solicitar los documentos.',
    },
    'auction.judgment.requestDocs.emptyList': {
        pt: 'Informe pelo menos um documento a ser solicitado.',
        en: 'Please list at least one document to request.',
        es: 'Informe al menos un documento a solicitar.',
    },
    'auction.judgment.requestDocs.missingAward': {
        pt: 'Adjudicação não localizada para este fornecedor.',
        en: 'Award not found for this supplier.',
        es: 'Adjudicación no encontrada para este proveedor.',
    },
    'auction.judgment.requestDocs.missingProposal': {
        pt: 'Proposta não localizada para este fornecedor — a diligência documental exige uma proposta submetida.',
        en: 'Proposal not found for this supplier — document diligence requires a submitted proposal.',
        es: 'Propuesta no encontrada para este proveedor — la diligencia documental requiere una propuesta presentada.',
    },
    'auction.judgment.requestDocs.pendingTitle': {
        pt: 'Você tem até {deadline} para apresentar os documentos solicitados',
        en: 'You have until {deadline} to submit the requested documents',
        es: 'Tiene hasta {deadline} para presentar los documentos solicitados',
    },
    // Bug #15 (2026-04-27): variante pra pregoeiro — antes ele via "Voce
    // tem ate..." e ficava achando que ELE precisava cumprir o prazo.
    'auction.judgment.requestDocs.pendingTitleAuctioneer': {
        pt: 'O fornecedor {supplier} tem até {deadline} para apresentar os documentos solicitados',
        en: 'Supplier {supplier} has until {deadline} to submit the requested documents',
        es: 'El proveedor {supplier} tiene hasta {deadline} para presentar los documentos solicitados',
    },
    'auction.judgment.requestDocs.expiredTitle': {
        pt: 'Prazo para envio de documentos expirado em {deadline}',
        en: 'Document submission deadline expired on {deadline}',
        es: 'Plazo para envío de documentos expirado el {deadline}',
    },
    'auction.judgment.requestDocs.fulfilledTitle': {
        pt: 'Documentos enviados pelo fornecedor em {date}',
        en: 'Documents submitted by supplier on {date}',
        es: 'Documentos enviados por el proveedor el {date}',
    },
    'auction.judgment.requestDocs.sectionTitle': {
        pt: 'Documentos Solicitados',
        en: 'Requested Documents',
        es: 'Documentos Solicitados',
    },
    'auction.judgment.requestDocs.empty': {
        pt: 'Nenhum documento complementar foi solicitado a este fornecedor.',
        en: 'No complementary documents have been requested from this supplier.',
        es: 'No se solicitaron documentos complementarios a este proveedor.',
    },
    'auction.judgment.requestDocs.remaining': {
        pt: 'Tempo restante: {time}',
        en: 'Time remaining: {time}',
        es: 'Tiempo restante: {time}',
    },

    // Judgment — Art. 46 LC 123/2006 (regularização postergada ME/EPP)
    'auction.judgment.art46.regularized': {
        pt: 'Regularização fiscal declarada em {date} (Art. 46 §1° LC 123/2006)',
        en: 'Fiscal regularization declared on {date} (Art. 46 §1° LC 123/2006)',
        es: 'Regularización fiscal declarada el {date} (Art. 46 §1° LC 123/2006)',
    },
    'auction.judgment.art46.expiredTitle': {
        pt: 'Prazo de regularização expirado',
        en: 'Regularization deadline expired',
        es: 'Plazo de regularización expirado',
    },
    'auction.judgment.art46.pendingTitle': {
        pt: 'Regularização fiscal pendente (Art. 46 LC 123/2006)',
        en: 'Fiscal regularization pending (Art. 46 LC 123/2006)',
        es: 'Regularización fiscal pendiente (Art. 46 LC 123/2006)',
    },
    'auction.judgment.art46.deadlinePrefix': {
        pt: 'Prazo:',
        en: 'Deadline:',
        es: 'Plazo:',
    },
    'auction.judgment.art46.remaining': {
        pt: '{time} restantes',
        en: '{time} remaining',
        es: '{time} restantes',
    },
    'auction.judgment.art46.markRegularizedButton': {
        pt: 'Declarar Regularizado',
        en: 'Mark as Regularized',
        es: 'Declarar Regularizado',
    },
    'auction.judgment.art46.declareDecayButton': {
        pt: 'Declarar Caducidade',
        en: 'Declare Decay',
        es: 'Declarar Caducidad',
    },
    'auction.judgment.art46.markRegularizedSuccess': {
        pt: '{supplier} — regularização fiscal registrada (Art. 46 §1° LC 123/2006)',
        en: '{supplier} — fiscal regularization recorded (Art. 46 §1° LC 123/2006)',
        es: '{supplier} — regularización fiscal registrada (Art. 46 §1° LC 123/2006)',
    },
    'auction.judgment.art46.declareDecaySuccess': {
        pt: 'Decadência declarada — {supplier} inabilitado (Art. 46 §2°)',
        en: 'Decay declared — {supplier} disqualified (Art. 46 §2°)',
        es: 'Caducidad declarada — {supplier} inhabilitado (Art. 46 §2°)',
    },
    'auction.judgment.art46.decayModalTitle': {
        pt: 'Declarar Caducidade — {supplier}',
        en: 'Declare Decay — {supplier}',
        es: 'Declarar Caducidad — {supplier}',
    },
    'auction.judgment.art46.decayModalDescription': {
        pt: 'Lote {lot}. O prazo de regularização fiscal expirou. Informe a justificativa formal (Art. 46 §2° LC 123/2006).',
        en: 'Lot {lot}. The fiscal regularization deadline has expired. Provide formal justification (Art. 46 §2° LC 123/2006).',
        es: 'Lote {lot}. El plazo de regularización fiscal ha expirado. Proporcione justificación formal (Art. 46 §2° LC 123/2006).',
    },
    'auction.judgment.art46.decayModalConfirmLabel': {
        pt: 'Declarar Caducidade',
        en: 'Declare Decay',
        es: 'Declarar Caducidad',
    },
    'auction.judgment.art46.decayModalPlaceholder': {
        pt: 'Justificativa detalhada da decadência...',
        en: 'Detailed decay justification...',
        es: 'Justificación detallada de la caducidad...',
    },

    // Decision — Art. 48 II LC 123/2006 (subcontractor declaration at homologation)
    'auction.decision.subcontractorModal.title': {
        pt: 'Declaração de Subcontratação ME/EPP',
        en: 'SME/EPP Subcontracting Declaration',
        es: 'Declaración de Subcontratación ME/EPP',
    },
    'auction.decision.subcontractorModal.description': {
        pt: 'Lote {lot}. Este lote exige subcontratação de ME/EPP (Art. 48 II LC 123/2006).',
        en: 'Lot {lot}. This lot requires SME/EPP subcontracting (Art. 48 II LC 123/2006).',
        es: 'Lote {lot}. Este lote exige subcontratación de ME/EPP (Art. 48 II LC 123/2006).',
    },
    'auction.decision.subcontractorModal.descriptionNoLot': {
        pt: 'Este lote exige subcontratação de ME/EPP (Art. 48 II LC 123/2006).',
        en: 'This lot requires SME/EPP subcontracting (Art. 48 II LC 123/2006).',
        es: 'Este lote exige subcontratación de ME/EPP (Art. 48 II LC 123/2006).',
    },
    'auction.decision.subcontractorModal.requiredPercentage': {
        pt: 'Percentual mínimo exigido: {pct}%.',
        en: 'Required minimum percentage: {pct}%.',
        es: 'Porcentaje mínimo exigido: {pct}%.',
    },
    'auction.decision.subcontractorModal.supplierIdLabel': {
        pt: 'ID/CNPJ do subcontratador ME/EPP',
        en: 'SME/EPP subcontractor ID/CNPJ',
        es: 'ID/CNPJ del subcontratista ME/EPP',
    },
    'auction.decision.subcontractorModal.supplierIdPlaceholder': {
        pt: 'Pesquisar fornecedor...',
        en: 'Search supplier...',
        es: 'Buscar proveedor...',
    },
    'auction.decision.subcontractorModal.actualPercentageLabel': {
        pt: 'Percentual efetivo de subcontratação (%)',
        en: 'Actual subcontracting percentage (%)',
        es: 'Porcentaje efectivo de subcontratación (%)',
    },
    'auction.decision.subcontractorModal.confirmButton': {
        pt: 'Declarar e Homologar',
        en: 'Declare and Homologate',
        es: 'Declarar y Homologar',
    },
    'auction.decision.subcontractorModal.cancelButton': {
        pt: 'Cancelar',
        en: 'Cancel',
        es: 'Cancelar',
    },
    'auction.decision.subcontractorModal.missingSupplier': {
        pt: 'Selecione o subcontratador ME/EPP',
        en: 'Select the SME/EPP subcontractor',
        es: 'Seleccione el subcontratista ME/EPP',
    },
    'auction.decision.subcontractorModal.missingPercentage': {
        pt: 'Informe o percentual de subcontratação (> 0)',
        en: 'Provide the subcontracting percentage (> 0)',
        es: 'Proporcione el porcentaje de subcontratación (> 0)',
    },
    'auction.decision.subcontractorModal.successMessage': {
        pt: 'Subcontratação declarada e adjudicação homologada',
        en: 'Subcontracting declared and award homologated',
        es: 'Subcontratación declarada y adjudicación homologada',
    },
} as const;

export type I18nKey = keyof typeof i18n;

export default i18n;
